import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Typography, FormGroup, FormControlLabel, Divider, Checkbox  } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useClickAway } from '@uidotdev/usehooks';
import { withQueryParams } from '@shared/commonFunctions';
import { useLenderId } from '@shared/hooks/useLenderId';
import { TabsPageHeader } from '@styles/Tools/LenderControl/LenderControlsHeaderStyles';
import { DealsButton } from '@styles/StyledButtons';
import { CancelButton } from '@shared/inputs/Buttons';
import { useAppSelector } from '@store/hooks';
import { selectCurrentState, updateCurrentState } from '@store/reducers/screenStateSlice';
import { LenderUtilityController } from '@styles/StyledDiv';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { LenderEditorTitle } from '../LenderEditor/LenderEditor';
import { InactivateLenderCardModal } from './InactivateLenderCardModal';
import { LenderSelectorPopper } from './LendersSelectorPopper';
import { LenderSelector } from './LenderSelector';

export const CREATE_LENDER = 'create';

export interface ILenderListItem {
  id: string;
  name: string;
  code: string;
  numericId: number;
  rank: number;
  subRank: number | null;
  acumaticaId: number | null;
  address: string;
  state: string;
  postCode: string;
  isActive: boolean;
}

export interface ILenderOptionListItem {
	id: string;
	name: string;
	code: string;
	numericId: number;
  }

interface ILenderControlsHeaderProps {
  title: string;
  showAddButton?: boolean;
}


export const LenderControlsHeader = ({ title, showAddButton }: ILenderControlsHeaderProps) => {
  const anchorDivRef = useRef<HTMLDivElement>(null);
  const typoRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const popperDivRef = useClickAway((event) => {
    if (!typoRef.current?.contains(event.target as Node)) {
      setOpen(false);
    }
  }) as React.RefObject<HTMLDivElement>;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLenderName, setSelectedLenderName] = useState<string | undefined>('Select Lender');
  const [selectedLender, setSelectedLender] = useState<ILenderListItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [activeLendersFilter, setActiveLendersFilter] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [openInactivateAlert, setInactivateAlert] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const lenderId = useLenderId();
  const dispatch = useDispatch();
  const selectedState = useAppSelector(selectCurrentState(title));
  const [options, setOptions] = useState<ILenderListItem[]>([]);
  const [baseOptions, setBaseOptions] = useState<ILenderListItem[]>([]);
  const { data: lendersList } = useSWR<ILenderListItem[]>('/lender-service/lenders');
  const displayCancelButton = lenderId === CREATE_LENDER;

  const handleOpenSelectionsPopper = () => {
    setAnchorEl(anchorDivRef?.current);
    setOpen(!open);
  };

  const handleActiveLendersFilter = () => {
    setActiveLendersFilter(!activeLendersFilter);
  };

  const handleInactivate = () => {
    const lender = options.find(option => option.id === lenderId);
    if (lender) {
        setSelectedLender(lender);
        setInactivateAlert(!openInactivateAlert);
    }
  };

  const updateOptions = (lenderId: string) => {
    const updatedOptions = options.map(option => {
        if (option.id === lenderId) {
            return { ...option, isActive: false };
        }
        return option;
    });
    setOptions(updatedOptions);
    setBaseOptions(updatedOptions);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'transition-popper' : undefined;

  const setLenderId = useCallback((lenderId: string) => {
    lenderId !== CREATE_LENDER && dispatch(updateCurrentState({ screenName: title, screenState: lenderId }));
    navigate(withQueryParams('.', { lenderId, tab: 0 }));
  },[title]);

  const onAddNewLender = () => {
    setLenderId(CREATE_LENDER);
  };

  useEffect(() => {
    if (lendersList) {
        const sorted = [...lendersList].sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1));
        const defaultSelected = (title === LenderEditorTitle) ? '' : sorted?.[0]?.id;
        setLenderId(selectedState || defaultSelected);
        setOptions(sorted);
        setBaseOptions(sorted);
    }
  }, [lendersList, selectedState, setLenderId, title]);

  useEffect(() => {
    if (lenderId && options.length > 0) {
      const findLender = baseOptions.find(option => option.id === lenderId);
      setSelectedLenderName(findLender?.name);
      if (findLender) {
        setIsActive(findLender.isActive);
      } else {
        setIsActive(false);
      }
    }
  }, [lenderId, options]);

  useEffect(() => {
    let filtered = [...baseOptions];

    if (activeLendersFilter) {
        filtered = filtered.filter(option => option.isActive === activeLendersFilter);
    }

    if (searchTerm) {
        filtered = filtered.filter(option => option.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    setOptions(filtered);
    setPageNumber(1);
  }, [activeLendersFilter, searchTerm, baseOptions]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  const itemsPerColumn = 8;
  const numColumns = 4;

  const pageCount = useMemo(() => {
    const totalItems = options.length;
    const itemsPerPage = itemsPerColumn * numColumns;
    return Math.ceil(totalItems / itemsPerPage);
  }, [options]);

  const paginatedOptions = useMemo(() => {
    const itemsPerPage = itemsPerColumn * numColumns;
    const startIndex = (pageNumber - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, options.length);
    return options.slice(startIndex, endIndex);
  }, [options, pageNumber]);

  function generateColumns(data: ILenderListItem[]) {
    const columns: ILenderListItem[][] = Array(numColumns).fill([]).map(() => []);

    data.forEach((item, index) => {
      const columnIndex = Math.floor(index / itemsPerColumn) % numColumns;
      columns[columnIndex].push(item);
    });
    return columns;
  }

  const columns = useMemo(() => generateColumns(paginatedOptions), [paginatedOptions]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageNumber(value);
  };

  const handleSelectLender = (option: ILenderListItem) => {
    setLenderId(option.id);
    setOpen(false);
  };

  return (
    <TabsPageHeader>
      <LoaderBackdrop open={loading} />
      <Typography variant="h3" sx={{ marginRight: '8px' }}>
        {title}
      </Typography>
      
      <LenderUtilityController ref={anchorDivRef} direction='row'>
        <LenderSelector open={open} typoRef={typoRef} id={id} isActive={isActive} selectedLenderName={selectedLenderName} handleOpenSelectionsPopper={handleOpenSelectionsPopper}/>
        <LenderSelectorPopper
          open={open}
          setOpen={setOpen}
          anchorEl={anchorEl}
          popperDivRef={popperDivRef}
          numColumns={numColumns}
          columns={columns}
          pageNumber={pageNumber}
          pageCount={pageCount}
          handleSelectLender={handleSelectLender}
          handleActiveLendersFilter={handleActiveLendersFilter}
          activeLendersFilter={activeLendersFilter}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchChange={handleSearchChange}
          handleChangePage={handleChangePage}
        />
        <FormGroup>
          <FormControlLabel control={<Checkbox checked={isActive} onChange={handleInactivate} />} disabled={!isActive} label="Active Lender" />
        </FormGroup>  
        <Divider flexItem orientation="vertical" sx={{ mx: 2 }}/>
        <Stack direction="row" marginLeft={1} gap={1}>
          {showAddButton && (
            <DealsButton size="medium" onClick={onAddNewLender}>
              Add New Lender
            </DealsButton>
          )}

          {displayCancelButton && (
            <CancelButton onClick={() => window.history.back()} />
          )}
        </Stack>
      </LenderUtilityController>
      {selectedLender && (
        <InactivateLenderCardModal
          lenderListItem={selectedLender}
          openInactivateAlert={openInactivateAlert}
          setOpenInactivateAlert={setInactivateAlert}
          setLoading={setLoading}
          setIsActive={setIsActive}
          updateOptions={updateOptions}
        />
      )}
    </TabsPageHeader>
  );
};